<template>
  <b-modal
    id="modal-booking-confirmation"
    scrollable
    title="Finale Prüfung - Buchung bestätigen"
    cancel-title="Abbrechen"
    ok-variant="success"
    ok-title="Ja, Buchung durchführen"
    cancel-variant="outline-secondary"
    modal-class="modal-success"
    size="xl"
    :ok-disabled="!validationResult.valid || isCheckingMailService"
    @ok="confirmBooking"
    @show="onModalShow"
    @hide="onModalHide"
  >
    <!-- Mail Service Status Indicator -->
    <div
      class="mb-4 p-3 border rounded"
      :class="{
        'border-success bg-light-success': mailServiceStatus.isOnline === true,
        'border-danger bg-light-danger': mailServiceStatus.isOnline === false,
        'border-warning bg-light-warning': mailServiceStatus.isOnline === null
      }"
    >
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon
            :icon="mailServiceStatus.isOnline === true ? 'CheckCircleIcon' :
              mailServiceStatus.isOnline === false ? 'XCircleIcon' : 'ClockIcon'"
            :class="{
              'text-success': mailServiceStatus.isOnline === true,
              'text-danger': mailServiceStatus.isOnline === false,
              'text-warning': mailServiceStatus.isOnline === null
            }"
            size="20"
          />
          <div class="ml-2">
            <h6 class="mb-0">
              E-Mail Service:
              <span
                :class="{
                  'text-success': mailServiceStatus.isOnline === true,
                  'text-danger': mailServiceStatus.isOnline === false,
                  'text-warning': mailServiceStatus.isOnline === null
                }"
              >
                {{ getMailServiceStatusText() }}
              </span>
            </h6>
            <small class="text-muted">
              <div>
                {{ mailServiceStatus.lastChecked ?
                  `Zuletzt geprüft: ${getFormattedTime(mailServiceStatus.lastChecked)}` :
                  'Status wird geprüft...' }}
              </div>
              <div
                v-if="mailServiceStatus.responseTime && mailServiceStatus.isOnline"
                class="text-success"
              >
                Antwortzeit: {{ mailServiceStatus.responseTime }}ms
              </div>
              <div
                v-if="mailServiceStatus.serviceInfo && mailServiceStatus.isOnline"
                class="text-info"
              >
                Version: {{ mailServiceStatus.serviceInfo.version || 'N/A' }}
                | Uptime: {{ formatUptime(mailServiceStatus.serviceInfo.uptime) }}
              </div>
            </small>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            v-if="mailServiceStatus.isOnline === false"
            variant="outline-primary"
            size="sm"
            :disabled="isCheckingMailService"
            @click="checkMailServiceStatus"
          >
            <b-spinner
              v-if="isCheckingMailService"
              small
              class="mr-1"
            />
            {{ isCheckingMailService ? 'Prüfe...' : 'Erneut prüfen' }}
          </b-button>
        </div>
      </div>
      <div
        v-if="mailServiceStatus.error"
        class="mt-2"
      >
        <small class="text-danger">{{ mailServiceStatus.error }}</small>
      </div>
    </div>

    <!-- Storage Availability Validation Results -->
    <div class="mb-4">
      <div
        v-if="validationResult.valid"
        class="alert alert-success"
      >
        <div class="d-flex align-items-center">
          <feather-icon
            icon="CheckCircleIcon"
            class="text-success mr-2"
            size="20"
          />
          <div>
            <strong>Validierung erfolgreich:</strong> {{ validationResult.summary }}
          </div>
        </div>
      </div>
      <div
        v-else
        class="alert alert-danger"
      >
        <div class="d-flex align-items-center mb-2">
          <feather-icon
            icon="AlertTriangleIcon"
            class="text-danger mr-2"
            size="20"
          />
          <div>
            <strong>Validierung fehlgeschlagen:</strong> {{ validationResult.summary }}
          </div>
        </div>
        <div
          v-if="validationResult.conflicts && validationResult.conflicts.length > 0"
          class="mt-3"
        >
          <strong>Konflikte:</strong>
          <ul class="mt-2 mb-0">
            <li
              v-for="conflict in validationResult.conflicts"
              :key="conflict.storageId"
              class="text-danger"
            >
              <strong>{{ conflict.name || `Lager ${conflict.storageId}` }}:</strong> {{ conflict.reason }}
              <span
                v-if="conflict.booking && conflict.booking.customer"
                class="text-muted"
              >
                (Kunde: {{ conflict.booking.customer.firstName }} {{ conflict.booking.customer.lastName }})
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Booking Overview -->
    <div class="booking-overview p-3 border rounded">
      <h4 class="mb-3">
        Buchungsübersicht
      </h4>

      <!-- Customer Information -->
      <div class="mb-4">
        <h5>Kundendaten</h5>
        <div class="row">
          <div class="col-md-6">
            <p class="mb-1">
              <strong>Name:</strong> {{ booking.customer.firstName }} {{ booking.customer.lastName }}
            </p>
            <p class="mb-1">
              <strong>E-Mail:</strong> {{ booking.customer.email }}
            </p>
            <p class="mb-1">
              <strong>Telefon:</strong> {{ booking.customer.mobileNumber || 'Nicht angegeben' }}
            </p>
          </div>
          <div class="col-md-6">
            <p class="mb-1">
              <strong>Adresse:</strong><br>
              {{ booking.customer.street }} {{ booking.customer.streetNumber }}<br>
              {{ booking.customer.zipCode }} {{ booking.customer.city }}
            </p>
          </div>
        </div>
      </div>

      <hr>

      <!-- Storage Information -->
      <div class="mb-4">
        <h5>Lagerinformationen</h5>
        <div v-if="booking.selectedStorages && booking.selectedStorages.length > 0">
          <b-table-lite
            :items="storageTableData"
            :fields="storageTableFields"
            responsive
            striped
            hover
            class="mb-0"
          >
            <template #cell(price)="data">
              {{ data.value }}€
            </template>
            <template #cell(status)="data">
              <b-badge
                :variant="data.item.statusVariant"
              >
                {{ data.value }}
              </b-badge>
            </template>
          </b-table-lite>
        </div>
        <div
          v-else
          class="text-muted"
        >
          Keine Lagereinheiten ausgewählt
        </div>
      </div>

      <hr>

      <!-- Summary -->
      <div class="row">
        <div class="col-md-6">
          <p class="mb-1">
            <strong>Anzahl Lagereinheiten:</strong> {{ storageCount }}
          </p>
          <p class="mb-1">
            <strong>Quelle:</strong> {{ sourceText }}
          </p>
        </div>
        <div class="col-md-6 text-right">
          <h5 class="mb-0">
            <strong>Gesamtsumme monatlich:</strong>
            <span class="text-success">{{ totalMonthlyPrice }}€</span>
          </h5>
        </div>
      </div>
    </div>

    <!-- Warning for conflicts -->
    <div
      v-if="!validationResult.valid"
      class="mt-3 p-3 border border-warning rounded bg-light-warning"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="AlertTriangleIcon"
          class="text-warning mr-2"
          size="20"
        />
        <div>
          <strong>Warnung:</strong> Es wurden Konflikte gefunden. Bitte beheben Sie diese vor der Buchung oder fahren Sie nur fort, wenn Sie sicher sind.
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton, BSpinner, BBadge, BTableLite } from 'bootstrap-vue'
import helper from '@/helper/helper'
import { emailService } from '@/service/emailService'

export default {
  name: 'BookingConfirmationModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    BBadge,
    BTableLite,
  },
  props: {
    booking: {
      type: Object,
      required: true
    },
    validationResult: {
      type: Object,
      required: true
    },
    parentRoute: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      mailServiceStatus: {
        isOnline: null,
        lastChecked: null,
        error: null,
        responseTime: null,
        serviceInfo: null,
        errorDetails: null
      },
      isCheckingMailService: false,
      healthCheckTimer: null,
      storageTableFields: [
        { key: 'name', label: 'Lager-ID' },
        { key: 'details', label: 'Details' },
        { key: 'price', label: 'Preis' },
        { key: 'location', label: 'Standort' },
        { key: 'status', label: 'Status' }
      ]
    }
  },
  computed: {
    storageCount() {
      return this.booking.selectedStorages ? this.booking.selectedStorages.length : 0
    },
    sourceText() {
      return this.parentRoute === 'offer' ? 'Angebot' : 'Direkte Buchung'
    },
    storageTableData() {
      if (!this.booking.selectedStorages) return []

      return this.booking.selectedStorages.map(storage => {
        const storageObj = typeof storage === 'object' ? storage : this.$store.getters.storageById(storage)
        if (!storageObj) return null

        // Check validation status for this storage
        const conflict = this.validationResult.conflicts?.find(c => c.storageId === storageObj.id)
        const isAvailable = !conflict

        return {
          name: storageObj.storageIdent || storageObj.name || `Lager ${storageObj.id}`,
          details: `${storageObj.size || 'N/A'} m²`,
          price: storageObj.price || 0,
          location: storageObj.location?.name || 'Unbekannt',
          status: isAvailable ? 'Verfügbar' : 'Konflikt',
          statusVariant: isAvailable ? 'success' : 'danger'
        }
      }).filter(Boolean)
    },
    totalMonthlyPrice() {
      if (!this.booking.selectedStorages) return 0

      return this.booking.selectedStorages.reduce((total, storage) => {
        const storageObj = typeof storage === 'object' ? storage : this.$store.getters.storageById(storage)
        return total + (storageObj?.price || 0)
      }, 0)
    }
  },
  beforeDestroy() {
    // Cleanup timer on component destruction
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer)
      console.log('🧹 Cleaned up SendGrid health check timer on destroy')
    }
  },
  methods: {
    onModalShow() {
      // Check mail service status when modal opens
      this.checkMailServiceStatus()

      // Set up periodic health checks every 30 seconds while modal is open
      this.healthCheckTimer = setInterval(() => {
        console.log('🔄 Periodic SendGrid health check')
        this.checkMailServiceStatus()
      }, 30000)
    },
    onModalHide() {
      // Clean up periodic health checks when modal closes
      if (this.healthCheckTimer) {
        clearInterval(this.healthCheckTimer)
        this.healthCheckTimer = null
        console.log('🛑 Stopped periodic SendGrid health checks')
      }
    },
    async checkMailServiceStatus() {
      const startTime = Date.now()
      this.isCheckingMailService = true
      this.mailServiceStatus.error = null

      // Log health check attempt
      console.log('🔍 BookingConfirmationModal: Starting SendGrid health check...')

      try {
        // Use real SendGrid service health check
        const healthResult = await emailService.checkHealth()
        const responseTime = Date.now() - startTime

        console.log('✅ SendGrid health check successful:', {
          healthy: healthResult.healthy,
          responseTime: `${responseTime}ms`,
          serviceData: healthResult.data
        })

        this.mailServiceStatus = {
          isOnline: healthResult.healthy,
          lastChecked: new Date().toISOString(),
          responseTime,
          serviceInfo: healthResult.data,
          error: healthResult.healthy ? null : 'E-Mail Service nicht erreichbar'
        }

        // Schedule automatic recheck if service is offline
        if (!healthResult.healthy) {
          setTimeout(() => {
            if (this.mailServiceStatus.isOnline === false) {
              console.log('🔄 Auto-retrying SendGrid health check after failure')
              this.checkMailServiceStatus()
            }
          }, 5000) // Retry after 5 seconds
        }
      } catch (error) {
        const responseTime = Date.now() - startTime
        console.error('❌ SendGrid health check failed:', {
          error: error.message,
          responseTime: `${responseTime}ms`,
          errorType: error.type || 'UNKNOWN'
        })
        // Enhanced error handling based on error type
        let errorMessage = 'E-Mail Service nicht erreichbar'
        if (error.message && error.message.includes('timeout')) {
          errorMessage = 'E-Mail Service Timeout - Server antwortet nicht'
        } else if (error.message && error.message.includes('Network Error')) {
          errorMessage = 'Netzwerkfehler - Bitte prüfen Sie Ihre Internetverbindung'
        } else if (error.code === 'ECONNREFUSED') {
          errorMessage = 'E-Mail Service ist offline oder nicht erreichbar'
        }
        this.mailServiceStatus = {
          isOnline: false,
          lastChecked: new Date().toISOString(),
          responseTime,
          error: errorMessage,
          errorDetails: error.message || 'Unbekannter Fehler'
        }
        // Auto-retry on network errors
        setTimeout(() => {
          if (this.mailServiceStatus.isOnline === false) {
            console.log('🔄 Auto-retrying SendGrid health check after error')
            this.checkMailServiceStatus()
          }
        }, 5000)
      } finally {
        this.isCheckingMailService = false
      }
    },
    getMailServiceStatusText() {
      if (this.mailServiceStatus.isOnline === true) return 'Online'
      if (this.mailServiceStatus.isOnline === false) return 'Offline'
      return 'Wird geprüft...'
    },
    getFormattedTime(isoString) {
      if (!isoString) return 'N/A'
      try {
        const { getFormattedTime } = helper()
        return getFormattedTime(isoString)
      } catch (error) {
        console.warn('getFormattedTime helper not found, using fallback')
        return new Date(isoString).toLocaleString()
      }
    },
    formatUptime(uptimeSeconds) {
      if (!uptimeSeconds) return 'N/A'

      const hours = Math.floor(uptimeSeconds / 3600)
      const minutes = Math.floor((uptimeSeconds % 3600) / 60)

      if (hours > 0) {
        return `${hours}h ${minutes}m`
      }
      return `${minutes}m`
    },
    confirmBooking() {
      this.$emit('booking-confirmed', {
        validationResult: this.validationResult,
        mailServiceStatus: this.mailServiceStatus
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-overview {
  background-color: #f8f9fa;
}

.bg-light-success {
  background-color: #d1edff !important;
}

.bg-light-danger {
  background-color: #f8d7da !important;
}

.bg-light-warning {
  background-color: #fff3cd !important;
}
</style>
